import React, { createContext, useContext, useState, ReactNode, useCallback } from 'react';

export interface ToastState {
  open: boolean;
  message: string;
  severity: 'success' | 'error';
}

export interface ToastContextProps {
  toast: ToastState;
  showToast: (message: string, severity: 'success' | 'error') => void;
  closeToast: () => void;
}

const ToastContext = createContext<ToastContextProps | undefined>(undefined);

export let dispatchToast: (message: string, severity: 'success' | 'error') => void = () => {};

export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const [toast, setToast] = useState<ToastState>({ open: false, message: '', severity: 'success' });

  const showToast = useCallback((message: string, severity: 'success' | 'error') => {
    setToast({ open: true, message, severity });
  }, []);

  const closeToast = useCallback(() => {
    setToast((prev) => ({ ...prev, open: false }));
  }, []);

  React.useEffect(() => {
    dispatchToast = showToast;
  }, [showToast]);

  return (
    <ToastContext.Provider value={{ toast, showToast, closeToast }}>
      {children}
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
};
